/**
 * Desktop Hover Variant Selection for Product Cards
 * Handles variant selection within the hover overlay on desktop
 */

class CardHoverVariantSelect {
  constructor() {
    this.init();
  }
  
  init() {
    // Set up all product cards on page load
    this.setupAllCards();
    
    // Re-initialize when cards are dynamically added (e.g., infinite scroll, filtering)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && node.classList.contains('card-wrapper')) {
              this.setupCard(node);
            } else if (node.querySelectorAll) {
              const cards = node.querySelectorAll('.card-wrapper');
              cards.forEach(card => this.setupCard(card));
            }
          }
        });
      });
    });
    
    // Observe the product grid for changes
    const productGrid = document.querySelector('.product-grid');
    if (productGrid) {
      observer.observe(productGrid, { childList: true, subtree: true });
    }
  }
  
  setupAllCards() {
    const cards = document.querySelectorAll('.card-wrapper');
    cards.forEach(card => this.setupCard(card));
  }
  
  setupCard(card) {
    // Check if already initialized
    if (card.hasAttribute('data-hover-initialized')) return;
    card.setAttribute('data-hover-initialized', 'true');
    
    const hoverOverlay = card.querySelector('.card__hover-overlay');
    if (!hoverOverlay) return;
    
    const form = hoverOverlay.querySelector('form');
    if (!form) return;
    
    const variantInput = form.querySelector('input[name="id"]');
    const variantSelect = form.querySelector('[data-variant-select]');
    const priceDisplay = form.querySelector('.btn-price');
    const addButton = form.querySelector('.card__hover-add-btn');
    
    if (!variantInput || !variantSelect || !priceDisplay) return;
    
    // Get all option inputs
    const optionInputs = form.querySelectorAll('.card__hover-option-input');
    
    // Update variant when options change
    optionInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateSelectedVariant(form, variantInput, variantSelect, priceDisplay, addButton);
      });
    });
    
    // Initialize variant on page load (set default selected variant)
    this.updateSelectedVariant(form, variantInput, variantSelect, priceDisplay, addButton);
    
    // Prevent hover overlay from triggering product link
    hoverOverlay.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }
  
  updateSelectedVariant(form, variantInput, variantSelect, priceDisplay, addButton) {
    // Get selected options
    const selectedOptions = {};
    const optionGroups = form.querySelectorAll('.card__hover-option-group');
    
    optionGroups.forEach(group => {
      const checkedInput = group.querySelector('.card__hover-option-input:checked');
      if (checkedInput) {
        const optionName = checkedInput.name.match(/options\[(.*?)\]/)[1];
        selectedOptions[optionName] = checkedInput.value;
      }
    });
    
    // Find matching variant
    const options = Array.from(variantSelect.options);
    let matchedVariant = null;
    
    for (const option of options) {
      const variantTitle = option.textContent.split(' - ')[0].trim();
      const optionValues = Object.values(selectedOptions);
      
      // Check if all selected options are in this variant
      const allMatch = optionValues.every(value => 
        variantTitle.includes(value.trim())
      );
      
      if (allMatch) {
        matchedVariant = option;
        break;
      }
    }
    
    // Update hidden input, select and price
    if (matchedVariant) {
      variantInput.value = matchedVariant.value;
      variantSelect.value = matchedVariant.value;
      const price = matchedVariant.getAttribute('data-price-formatted');
      if (price && priceDisplay) {
        priceDisplay.textContent = price;
      }
      
      // Enable/disable add button based on availability
      if (addButton) {
        if (matchedVariant.disabled) {
          addButton.disabled = true;
          const btnText = addButton.querySelector('.btn-text');
          if (btnText) btnText.textContent = 'Sold Out';
        } else {
          addButton.disabled = false;
          const btnText = addButton.querySelector('.btn-text');
          if (btnText) btnText.textContent = 'Add to Cart';
        }
      }

      // Update macros display for the selected variant
      this.updateCardMacros(form, matchedVariant);
    }
  }

  updateCardMacros(formEl, selectedOption) {
    const cardWrapper = formEl.closest('.card-wrapper') || formEl.closest('.card');
    if (!cardWrapper) return;

    const macrosContainer = cardWrapper.querySelector('.card__macros');
    if (!macrosContainer) return;

    const cal = selectedOption.getAttribute('data-calories') || '0';
    const protein = selectedOption.getAttribute('data-protein') || '0';
    const carbs = selectedOption.getAttribute('data-carbs') || '0';
    const fat = selectedOption.getAttribute('data-fat') || '0';

    const calEl = macrosContainer.querySelector('[data-macro="calories"]');
    const proteinEl = macrosContainer.querySelector('[data-macro="protein"]');
    const carbsEl = macrosContainer.querySelector('[data-macro="carbs"]');
    const fatEl = macrosContainer.querySelector('[data-macro="fat"]');

    if (calEl) calEl.innerHTML = `${cal} <span class="card__macro-label">CAL</span>`;
    if (proteinEl) proteinEl.innerHTML = `${protein} <span class="card__macro-label">P</span>`;
    if (carbsEl) carbsEl.innerHTML = `${carbs} <span class="card__macro-label">C</span>`;
    if (fatEl) fatEl.innerHTML = `${fat} <span class="card__macro-label">F</span>`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardHoverVariantSelect();
  });
} else {
  new CardHoverVariantSelect();
}

// Also re-initialize after AJAX updates (for filtered collections, etc.)
document.addEventListener('ajaxComplete', () => {
  new CardHoverVariantSelect();
});

